import React, { useCallback, useEffect, useState } from 'react';
import Button from '../../components/common/Button';
import { apiService } from '../../services/api';
import { Competition } from '../../types/interfaces';
import { PresentList, errorMessage, fetchPresentList } from '../../services/manageApi';
import { ManageTab, ManageTabId } from './manageTabs';

interface AccessEntry {
  access_type: string;
  current_votes?: number | null;
  max_votes?: number | null;
  completed_evaluations?: number | null;
  max_evaluations?: number | null;
}

interface Stats {
  entries: number;
  submitted: number;
  judges: number;
  evaluationsDone: number;
  evaluationCapacity: number;
}

export function computeStats(present: PresentList, access: AccessEntry[]): Stats {
  const judges = access.filter((entry) => entry.access_type === 'judge');
  return {
    entries: present.entries.length,
    submitted: present.entries.filter((entry) => entry.has_submission).length,
    judges: judges.length,
    evaluationsDone: judges.reduce((sum, judge) => sum + (judge.completed_evaluations ?? judge.current_votes ?? 0), 0),
    evaluationCapacity: judges.reduce((sum, judge) => sum + (judge.max_evaluations ?? judge.max_votes ?? 0), 0),
  };
}

const StatTile: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <div className="manage-stat">
    <span className="manage-stat__value">{value ?? '…'}</span>
    <span className="manage-stat__label">{label}</span>
  </div>
);

interface Props {
  competition: Competition;
  shortcuts: ManageTab[];
  onOpenTab: (tab: ManageTabId) => void;
}

const OverviewTab: React.FC<Props> = ({ competition, shortcuts, onOpenTab }) => {
  const [stats, setStats] = useState<Stats | null>(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const [present, access] = await Promise.all([
        fetchPresentList(competition.id),
        apiService.get(`/competitions/${competition.id}/access`),
      ]);
      setStats(computeStats(present, access.data?.entries || []));
    } catch (err) {
      setStats(null);
      setError(errorMessage(err, "Couldn't load summary numbers"));
    }
  }, [competition.id]);

  useEffect(() => {
    load();
  }, [load]);

  const entryLabel = competition.competition_mode === 'team' ? 'Teams' : 'Participants';

  return (
    <div className="manage-overview">
      {error ? (
        <div className="manage-error" role="alert">
          {error}{' '}
          <Button variant="ghost" size="sm" onClick={load}>Retry</Button>
        </div>
      ) : (
        <div className="manage-stats" data-tour="manage-stats" aria-busy={!stats}>
          <StatTile label={entryLabel} value={stats?.entries} />
          <StatTile label="Submitted" value={stats?.submitted} />
          <StatTile label="Judges" value={stats?.judges} />
          <StatTile label="Evaluations done" value={stats ? `${stats.evaluationsDone} / ${stats.evaluationCapacity}` : undefined} />
        </div>
      )}
      <h3>Go to</h3>
      <div className="manage-shortcuts" data-tour="manage-shortcuts">
        {shortcuts.map((tab) => (
          <button key={tab.id} type="button" className="manage-shortcut" onClick={() => onOpenTab(tab.id)}>
            <strong>{tab.label}</strong>
            <span>{tab.description}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default OverviewTab;
